import {
    Field,
    FieldDescription,
    FieldError,
    FieldLabel,
} from "@/components/shadcn/field";
import { useTranslations } from "next-intl";
import { ReactNode } from "react";
import {
    Control,
    Controller,
    ControllerFieldState,
    ControllerRenderProps,
    FieldPath,
    FieldValues,
    UseFormStateReturn,
} from "react-hook-form";

export type FormFieldCommonProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
    control: Control<TFieldValues>;
    name: TName;
    label: string;
    disabled?: boolean;
    required?: boolean;
    description?: string;
    showOptionalText?: boolean;
};

export function FormFieldBase<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    control,
    name,
    label,
    disabled = false,
    required = false,
    showOptionalText = false,
    description,
    renderField,
    renderError,
}: FormFieldCommonProps<TFieldValues, TName> & {
    renderField: (
        field: ControllerRenderProps<TFieldValues, TName>,
        fieldState: ControllerFieldState,
        disabled: boolean,
    ) => ReactNode;
    renderError?: (
        field: ControllerRenderProps<TFieldValues, TName>,
        fieldState: ControllerFieldState,
        formState: UseFormStateReturn<TFieldValues>,
    ) => ReactNode;
}) {
    const t = useTranslations("common");
    const optionalText = t("optional");

    return (
        <Controller
            control={control}
            name={name}
            render={({ field, fieldState, formState }) => (
                <Field
                    data-invalid={fieldState.invalid}
                    data-disabled={disabled || undefined}
                >
                    <FieldLabel htmlFor={field.name}>
                        <span>
                            {label}
                            {required ? (
                                <span className="text-primary ml-1">*</span>
                            ) : (
                                showOptionalText && (
                                    <span className="text-muted-foreground ml-1 font-light">
                                        {optionalText}
                                    </span>
                                )
                            )}
                        </span>
                    </FieldLabel>
                    {renderField(field, fieldState, disabled)}
                    {description && (
                        <FieldDescription>{description}</FieldDescription>
                    )}
                    {renderError
                        ? renderError(field, fieldState, formState)
                        : fieldState.invalid && (
                              <FieldError errors={[fieldState.error]} />
                          )}
                </Field>
            )}
        />
    );
}
